import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { api } from './api'

export interface Language {
  code: string
  name: string
  native_name: string
  tts_voice: string
  rtl: boolean
}

export interface Market {
  code: string
  name: string
  primary_language: string
  languages: string[]
  currency: string
  ypp_available: boolean
}

export function useLanguages() {
  return useQuery({
    queryKey: ['i18n', 'languages'],
    queryFn: () => api.get<Language[]>('/i18n/languages'),
    staleTime: 1000 * 60 * 60,
  })
}

export function useMarkets() {
  return useQuery({
    queryKey: ['i18n', 'markets'],
    queryFn: () => api.get<Market[]>('/i18n/markets'),
    staleTime: 1000 * 60 * 60,
  })
}

export function LanguageSelect({ value, onChange, id }: {
  value: string
  onChange: (v: string) => void
  id?: string
}) {
  const { data: languages } = useLanguages()
  return (
    <select id={id} value={value} onChange={(e) => onChange(e.target.value)}>
      {(languages || [{ code: 'en', name: 'English', native_name: 'English' }]).map((l: any) => (
        <option key={l.code} value={l.code}>
          {l.name}{l.native_name && l.native_name !== l.name ? ` (${l.native_name})` : ''}
        </option>
      ))}
    </select>
  )
}

export function MarketSelect({ value, onChange, id }: {
  value: string
  onChange: (v: string) => void
  id?: string
}) {
  const { data: markets } = useMarkets()
  return (
    <select id={id} value={value} onChange={(e) => onChange(e.target.value)}>
      {(markets || [{ code: 'US', name: 'United States' }]).map((m: any) => (
        <option key={m.code} value={m.code}>{m.name}</option>
      ))}
    </select>
  )
}

export const csvToArr = (s: string): string[] =>
  (s || '').split(',').map((x) => x.trim()).filter(Boolean)

export function LanguageMultiSelect({ value, onChange }: {
  value: string[]
  onChange: (v: string[]) => void
}) {
  const { data: languages } = useLanguages()
  const items = (languages || []).map((l: any) => ({
    code: l.code,
    name: l.native_name && l.native_name !== l.name ? `${l.name} (${l.native_name})` : l.name,
  }))
  return <MultiSelect items={items} value={value} onChange={onChange} placeholder="Search language..." />
}

export function MarketMultiSelect({ value, onChange }: {
  value: string[]
  onChange: (v: string[]) => void
}) {
  const { data: markets } = useMarkets()
  const items = (markets || []).map((m: any) => ({ code: m.code, name: m.name }))
  return <MultiSelect items={items} value={value} onChange={onChange} placeholder="Search country..." />
}

export function MultiSelect({ items, value, onChange, placeholder }: {
  items: { code: string; name: string }[]
  value: string[]
  onChange: (v: string[]) => void
  placeholder?: string
}) {
  const [filter, setFilter] = useState('')
  const f = filter.toLowerCase()
  const shown = items.filter(
    (i) => !f || i.name.toLowerCase().includes(f) || i.code.toLowerCase().includes(f)
  )
  const toggle = (code: string) =>
    onChange(value.includes(code) ? value.filter((v) => v !== code) : [...value, code])
  return (
    <div className="multiselect">
      <div className="ms-actions">
        <input
          placeholder={placeholder || 'Search...'}
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
        />
        <button type="button" className="btn btn-secondary btn-sm" onClick={() => onChange(items.map((i) => i.code))}>
          All ({items.length})
        </button>
        <button type="button" className="btn btn-secondary btn-sm" onClick={() => onChange([])}>
          Clear
        </button>
      </div>
      <div className="ms-count">{value.length} selected{value.length ? `: ${value.join(', ')}` : ''}</div>
      <div className="ms-list">
        {shown.map((i) => (
          <label key={i.code} className="ms-item">
            <input type="checkbox" checked={value.includes(i.code)} onChange={() => toggle(i.code)} />
            <span>{i.name} <code>{i.code}</code></span>
          </label>
        ))}
      </div>
    </div>
  )
}
